import { useState } from "react";
import OtpInput from "react-otp-input";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";

import { Toast } from "../../../utils/Toast";
import { FullPageLoader } from "../../../utils/FullPageLoader";
import { twoFactorService } from "../../../services/two-factor.service";
import { IApiErrorPayload, needsPhoneNumber } from "../constants/auth-error.constants";

const OTP_LENGTH = 6;

interface IProps {
    /** The 406 body that started this challenge. */
    challenge: IApiErrorPayload;
    /** Called with the verified challenge token so the caller can finish its
     *  own flow — completing a login, or retrying a follow request. */
    onVerified: (challengeToken: string, otpCode: string) => Promise<void> | void;
    onCancel: () => void;
}

export const TwoFactorModal: React.FC<IProps> = ({ challenge, onVerified, onCancel }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [otp, setOtp] = useState("");
    const [phone, setPhone] = useState<string | undefined>();
    const [phoneHint, setPhoneHint] = useState(challenge.phoneHint ?? "");
    const [deliveryFailed, setDeliveryFailed] = useState(!!challenge.deliveryFailed);
    // Starts on the phone step only when the account has no verified number.
    const [needsPhone, setNeedsPhone] = useState(needsPhoneNumber(challenge));

    const challengeToken = challenge.challengeToken ?? "";

    const submitPhone = async () => {
        if (!phone || !isValidPhoneNumber(phone)) {
            Toast.fire({ icon: "warning", title: "Enter a valid phone number with its country code." });
            return;
        }
        setIsLoading(true);
        try {
            const res: any = await twoFactorService.submitPhone({ challengeToken, phone });
            setPhoneHint(res?.payload?.phoneHint ?? "");
            setDeliveryFailed(!res?.payload?.delivered);
            setNeedsPhone(false);
            Toast.fire({ icon: "success", title: res?.message ?? "Code sent." });
        } catch (error: any) {
            Toast.fire({
                icon: "warning",
                title: error?.response?.data?.message || "We could not send your code. Please try again.",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const submitOtp = async () => {
        if (otp.length !== OTP_LENGTH) {
            Toast.fire({ icon: "warning", title: "Enter the full code." });
            return;
        }
        setIsLoading(true);
        try {
            await onVerified(challengeToken, otp);
        } catch (error: any) {
            // A spent or expired challenge cannot be retried in place — the
            // caller has to start over from the beginning.
            setOtp("");
            Toast.fire({
                icon: "warning",
                title: error?.response?.data?.message || "That code did not work. Please try again.",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const resend = async () => {
        setIsLoading(true);
        try {
            const res: any = await twoFactorService.resend({ challengeToken });
            setDeliveryFailed(!res?.payload?.delivered);
            Toast.fire({ icon: "success", title: res?.message ?? "We sent you a new code." });
        } catch (error: any) {
            Toast.fire({
                icon: "warning",
                title: error?.response?.data?.message || "Could not resend the code.",
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="up-modal up-twofactormodal">
            <div className="up-modalcontent up-newtwofamodalbody">
                {isLoading && <FullPageLoader />}
                <div className="up-formHead up-modalheadmarginzero">
                    <h2>{needsPhone ? "Verify your phone number" : "Enter your code"}</h2>
                    <span>
                        {needsPhone
                            ? "This organization requires a verified phone number. We will text you a code."
                            : `We sent a ${OTP_LENGTH}-digit code to ${phoneHint}.`}
                    </span>
                </div>

                {/* Codes are SMS-only — there is no fallback channel, so a
                    delivery failure has to be visible and retryable. */}
                {deliveryFailed && !needsPhone && (
                    <div className="up-description">
                        <p>We could not send your code. Use Resend below to try again.</p>
                    </div>
                )}

                {needsPhone ? (<>
                    <div className="form-group">
                        <label>Phone number</label>
                        <PhoneInput
                            international
                            value={phone}
                            onChange={setPhone}
                            className="form-control"
                            placeholder="Enter your phone number"
                        />
                    </div>
                    <div className="form-group up-flexcenterbtn">
                        <button type="button" className="up-btn" onClick={submitPhone}>
                            Send code
                        </button>
                        <div className="up-alreadyahveaccount">
                            <a onClick={onCancel} href="javascript:void(0);">
                                Cancel
                            </a>
                        </div>
                    </div>
                    </>
                ) : (
                    <>
                        <div className="up-flexcenter form-group">
                            <OtpInput
                                inputStyle={{ width: "3rem", padding: "0" }}
                                value={otp}
                                onChange={setOtp}
                                numInputs={OTP_LENGTH}
                                isInputNum
                                shouldAutoFocus
                                separator={<span>-</span>}
                            />
                        </div>
                        <div className="form-group up-flexcenterbtn">
                            <button type="button" className="up-btn" onClick={submitOtp}>
                                Verify
                            </button>
                            <div className="up-alreadyahveaccount">
                                <a onClick={onCancel} href="javascript:void(0);">
                                    Cancel
                                </a>
                            </div>
                        </div>
                        <div className="up-alreadyahveaccount">
                            <h2>
                                Didn't get it?{" "}
                                <a onClick={resend} href="javascript:void(0);">
                                    Resend code
                                </a>
                            </h2>
                        </div>
                    </>
                )}

                
            </div>
        </div>
    );
};

export default TwoFactorModal;
